import axios from 'axios';
import { googleLogout } from '@react-oauth/google';
import { setTokens, clearAuth, fetchProfile } from './slices/authSlice';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/v1';

const api = axios.create({ baseURL: API_URL });

// Prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise = null;
let waitQueue = [];

function drainQueue(token, error) {
  waitQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
  waitQueue = [];
}

function isTokenExpiringSoon(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return (payload.exp * 1000 - Date.now()) < 5 * 60 * 1000;
  } catch {
    return true;
  }
}

const AUTH_ENDPOINTS = [
  '/auth/login', '/auth/register', '/auth/refresh-tokens',
  '/auth/forgot-password', '/auth/reset-password', '/auth/verify-email',
];

function isAuthEndpoint(url) {
  return AUTH_ENDPOINTS.some(e => url?.includes(e));
}

function doRefresh(store) {
  // Deduplicate: if a refresh is already in flight return the same promise
  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = (async () => {
    const { auth } = store.getState();
    if (!auth?.refreshToken) throw new Error('no_refresh');

    const { data } = await axios.post(`${API_URL}/auth/refresh-tokens`, {
      refreshToken: auth.refreshToken,
    });
    const accessToken = data.access?.token || data.tokens?.access?.token;
    const refreshToken = data.refresh?.token || data.tokens?.refresh?.token;
    if (!accessToken) throw new Error('no_token');

    store.dispatch(setTokens({ accessToken, refreshToken }));
    store.dispatch(fetchProfile());
    return accessToken;
  })()
    .then((token) => { drainQueue(token, null); return token; })
    .catch((err) => { drainQueue(null, err); throw err; })
    .finally(() => { isRefreshing = false; refreshPromise = null; });

  return refreshPromise;
}

export function setupAxiosInterceptors(store) {
  // ── Request interceptor: attach token, proactively refresh if expiring soon ──
  api.interceptors.request.use(async (config) => {
    const { auth } = store.getState();
    let { accessToken } = auth;

    if (!isAuthEndpoint(config.url) && accessToken && isTokenExpiringSoon(accessToken) && auth.refreshToken) {
      try {
        accessToken = await doRefresh(store);
      } catch {
        // If proactive refresh fails, let the request go through;
        // the 401 response handler will catch it.
      }
    }

    if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
    return config;
  });

  // ── Response interceptor: reactive refresh on 401 ────────────────────────────
  api.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error.config;

      if (isAuthEndpoint(original.url)) return Promise.reject(error);

      if (error.response?.status === 401 && !original._retry) {
        original._retry = true;

        // If a refresh is already in flight, queue behind it
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            waitQueue.push({ resolve, reject });
          }).then((token) => {
            original.headers.Authorization = `Bearer ${token}`;
            return api(original);
          });
        }

        try {
          const newToken = await doRefresh(store);
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original);
        } catch {
          store.dispatch(clearAuth());
          googleLogout();
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
}

export default api;
